import * as _ from 'lodash';
import { useRef } from 'react';
import { IDonor, IEwtSummary, IMatchRunPosition, IOneWay, IPosition, IRecipient, ISingleEpletMmRisk, ISoleSourceDonor, ISwapFailureAnalysis, ITargetItem } from '@nkr/models/index';
import { DonationType, MatchRunType, MatchStatus, PatientType } from '@nkr/enums/index';
import { Button, Glyphicon, OverlayTrigger } from 'react-bootstrap';
import { SsdTableWrapper } from './styles';
import { renderCenter } from './center-with-swap-failures';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import BridgeDonorRow from './bridge-donor-row';
import AlleleMatch from '@nkr/components/allele-match';
import PopoverTemplate from '@nkr/components/popover-template';
import LockingMatchPopover from './locking-match-popover';

interface IProps {
    position: IMatchRunPosition,
    swapFailures: ISwapFailureAnalysis[],
    swapIndex: number,
    chainIndex: number,
    pathIndex: number,
    pathLength: number,
    matchRunType: MatchRunType,
    confirmRexm: boolean,
    showRexm: boolean,
    oneWays: IOneWay[],
    epletMmRiskList: ISingleEpletMmRisk[],
    onRequestExm: (oneWayId: number) => void,
    onShowOverlay: (donor: IDonor, recipient: IRecipient, soleSourceDonors: ISoleSourceDonor[], ssdHtml: string, target: any, intendedDonors: IDonor[]) => void,
    onHideOverlay: () => void,
    onShowEpletMmGraphData: (recipient: IRecipient, rowRef: any) => void,
    onShowAcceptsData: (donor: IDonor, rowRef: any) => void,
    onShowMortalityBreakdown?: (recipient: IRecipient) => void,
    onShowEwt: (summary: IEwtSummary, initialCount: any) => void,
    onShowFwt: (recipient: IRecipient) => void
}

const PositionRow = (props: IProps) => {
    const rowRef = useRef(null);

    const handleShowEpletGraph = () => {
        props.onShowEpletMmGraphData(props.position.recipient,/*props.position.recipient.center.id, props.position.recipient.id,*/ rowRef.current);
    };

    const handleShowAcceptsData = () => {
        props.onShowAcceptsData(props.position.donor, rowRef.current);
    };

    const renderAbo = () => {
        let donorBloodType = props.position.oneWay.donor.bloodType;
        let recipientBloodType = props.position.oneWay.recipient.bloodType;

        let isOToNonO = donorBloodType == 'O' && recipientBloodType != 'O';
        let isNonOToO = donorBloodType != 'O' && recipientBloodType == 'O';

        let className = ''

        if (isOToNonO) {
            className = 'label label-minimize label-danger';
        }

        if (isNonOToO) {
            className = 'label label-minimize label-success';
        }

        return (
            <span className={className}>{position.oneWay.donor.bloodTypeDisplay}&nbsp;:&nbsp;{position.oneWay.recipient.bloodType}</span>
        );
    };

    const renderAges = () => {
        let { bloodType: donorBloodType, age: donorAge } = props.position.oneWay.donor;
        let { bloodType: recipientBloodType, age: recipientAge } = props.position.oneWay.recipient

        let ageDifference = parseInt(recipientAge) - donorAge;

        let isDonorOlderThanPatient = donorAge > parseInt(recipientAge);
        let isDonorMoreThan10YearsYoungerThanPatient = ageDifference > 10;
        let isDonorLessThan10YearsYoungerThanPatient = ageDifference <= 10;

        let className = '';

        if (donorBloodType == 'O' && recipientBloodType == 'O' && isDonorMoreThan10YearsYoungerThanPatient) {
            className = 'label label-minimize label-danger';
        }

        if (donorBloodType == 'O' && recipientBloodType == 'O' && (isDonorOlderThanPatient || isDonorLessThan10YearsYoungerThanPatient)) {
            className = 'label label-minimize label-success';
        }

        if (parseInt(oneWay.recipient.age) <= 21) {
            className = 'label label-minimize label-black';
        }

        return (
            <span className={className}>{props.position.oneWay.donor.age}:{props.position.oneWay.recipient.age}</span>
        );
    };

    const renderRecipientType = () => {
        let { isRealTimeSwapFailure, patientTypeId } = props.position.oneWay.recipient

        let className = '';

        if (isRealTimeSwapFailure || patientTypeId == PatientType.Waitlist || patientTypeId == PatientType.KidneyReplacementCase) {
            className = 'label label-minimize label-black';
        }

        return (
            <span className={className}>{props.position.oneWay.recipient.patientTypeAbbreviation}</span>
        );
    };

    const renderDDR = () => {
        let { declineRatioHistoric, declineRatioCurrent, center, id } = props.position.oneWay.recipient;

        let ddrType = (declineRatioHistoric > (declineRatioCurrent)) ? 2 : 1;
        let donorDeclineLink = `/center/${center.id}/recipient/${id}/profile/donor-declines?ddrtype=${ddrType}`;

        return (
            <a href={donorDeclineLink} target='_blank' rel='noopener noreferrer'>
                {_.round(position.oneWay.recipient.highestDeclineRatio)}
            </a>
        );
    }

    const renderCpra4 = () => {
        let className = '';

        if ((position.oneWay.recipient.cpra4 != null ? parseFloat(position.oneWay.recipient.cpra4.toFixed(2)) : 0) >= 99) {
            className = 'label label-minimize label-red';
        }

        return (
            <span className={className}>{position.oneWay.recipient.cpra4 != null ? position.oneWay.recipient.cpra4.toFixed(2) : '--'}</span>
        );
    };

    const renderHeights = () => {
        let donorHeight = props.position.oneWay.donor.height;
        let recipientHeight = props.position.oneWay.recipient.height;
        let diff = donorHeight - recipientHeight;
        let isShorter = diff < 0;

        if (donorHeight > 0 && recipientHeight > 0) {
            if (isShorter && Math.abs(diff) >= 6) {
                return <span className='label label-minimize label-success'>{props.position.oneWay.donor.height}:{props.position.oneWay.recipient.height}</span>
            }

            if (!isShorter && Math.abs(diff) >= 6) {
                return <span className='label label-minimize label-danger'>{props.position.oneWay.donor.height}:{props.position.oneWay.recipient.height}</span>
            }
        } else if (donorHeight == 0 || recipientHeight == 0) {
            let title = '';

            if (donorHeight == 0 && recipientHeight > 0) {
                title = 'Donor height not specified';
            }

            if (donorHeight > 0 && recipientHeight == 0) {
                title = 'Recipient height not specified';
            }

            if (donorHeight == 0 && recipientHeight == 0) {
                title = 'Donor height and recipient height not specified';
            }

            return <span className='label label-minimize label-error' title={title} style={{ cursor: 'pointer' }}>{props.position.oneWay.donor.height}:{props.position.oneWay.recipient.height}</span>
        }

        return (
            <span>{props.position.oneWay.donor.height}:{props.position.oneWay.recipient.height}</span>
        );
    };

    const renderExm = () => {
        let greenList = ['PNFC', 'NFC', 'NR'];
        let yellowList = ['IP'];

        if (props.position.oneWay.exmDisplayString != null && props.position.oneWay.exmDisplayString != '') {
            let containsGreen = greenList.filter(x => props.position.oneWay.exmDisplayString.indexOf(x) >= 0).length > 0;
            let containsYellow = yellowList.some(x => props.position.oneWay.exmDisplayString.includes(x));

            if (containsGreen) {
                return (
                    <Button
                        bsStyle='success'
                        bsSize='xs'
                        style={{ textAlign: 'center' }}
                        disabled={false}
                        onClick={() => props.onRequestExm(props.position.oneWayId)}>{props.position.oneWay.exmDisplayString}</Button>
                );
            } else if (containsYellow) {
                return (
                    <Button
                        bsSize='xs'
                        className="btn ip-btn-yellow"
                        style={{ textAlign: 'center' }}>{props.position.oneWay.exmDisplayString}</Button>
                );
            }
            else {
                return (
                    <Button
                        bsSize='xs'
                        style={{ textAlign: 'center' }}>{props.position.oneWay.exmDisplayString}</Button>
                );
            }
        } else {
            return (
                <Button
                    bsStyle='success'
                    bsSize='xs'
                    style={{ textAlign: 'center' }}
                    disabled={false}
                    onClick={() => props.onRequestExm(props.position.oneWayId)}>NR</Button>
            );
        }
    };

    const renderDonorInformation = (index: number, donor: IDonor) => {
        return (
            <PopoverTemplate width='1000px' dismiss={false}>
                <SsdTableWrapper condensed striped bordered>
                    <thead>
                        <tr>
                            {index == 1 && (<>
                                <th>Earliest Donation Date</th>
                                <th>Latest Donation Date</th>
                                <th>Min</th>
                                <th>Max</th>
                            </>)
                            }
                            <th>FAnat</th>
                            <th>CC</th>
                            <th>E</th>
                            <th>C</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {index == 1 && (<>
                                <td>{donor.donationStartDate}</td>
                                <td>{donor.donationEndDate}</td>
                                <td>{donor.minWeeksUntilTransplant}</td>
                                <td>{donor.maxWeeksUntilTransplant}</td>
                            </>)
                            }
                            <td>{getWhichKidney(donor)}</td>
                            <td>{donor.creatinineClearance}</td>
                            <td>{donor.ebv}</td>
                            <td>{donor.cmv}</td>
                        </tr>
                    </tbody>
                </SsdTableWrapper>
            </PopoverTemplate>
        );
    };

    const renderNonChainStartInfo = (donor: IDonor) => {
        return (
            <PopoverTemplate width='1000px' dismiss={false}>
                <SsdTableWrapper condensed striped bordered>
                    <thead>
                        <tr>
                            <th>FAnat</th>
                            <th>CC</th>
                            <th>E</th>
                            <th>C</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{getWhichKidney(donor)}</td>
                            <td>{donor.creatinineClearance}</td>
                            <td>{donor.ebv}</td>
                            <td>{donor.cmv}</td>
                        </tr>
                    </tbody>
                </SsdTableWrapper>
            </PopoverTemplate>
        );
    };

    const getWhichKidney = (donor: IDonor) => {
        if (donor.whichKidney != '' && donor.whichKidney != 'DNK') {
            let results = `${donor.whichKidneyAbbreviated}${donor.numberOfArteries}`;

            if (donor.whichKidney == 'L-opt') {
                results += '(R)';
            }

            if (donor.whichKidney == 'R-opt') {
                results += '(L)';
            }

            return results;
        }

        return '--';
    };

    const renderChainStartPopover = (index: number, donor: IDonor) => {
        let alias = donor.alias;

        var showDonorInfo = donor.isAdp == true || position.oneWay.donor.donationTypeId == DonationType.Bridge || position.oneWay.donor.donationTypeId == DonationType.NonDirectedDonor;

        if (showDonorInfo) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <OverlayTrigger placement='top' overlay={renderDonorInformation(index, donor)}>
                        <a href={`/center/${donor.center.id}/donor/${donor.id}/profile`} target='_blank'>{alias}</a>
                    </OverlayTrigger>
                    <LockingMatchPopover donor={donor} />
                </div>
            );
        }
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <OverlayTrigger placement='top' overlay={renderNonChainStartInfo(donor)}>
                    <a href={`/center/${donor.center.id}/donor/${donor.id}/profile`} target='_blank'>{alias}</a>
                </OverlayTrigger>
                <LockingMatchPopover donor={donor} />
            </div>
        );
    };

    const renderBridgeDonors = () => {
        let bridgeDonors = props.position.oneWay.recipient.donors.filter(x => x.statusId <= MatchStatus.Transplanted && x.isExportable);

        return (
            <>
                {bridgeDonors.map((bridgeDonor, index) => {
                    let swapFailure = props.swapFailures.find(x => x.centerId == bridgeDonor.center.id);

                    return (
                        <BridgeDonorRow
                            key={`ch-disp-swap-${props.swapIndex}-chain-${props.chainIndex}-bd-${index}`}
                            position={position}
                            bridgeDonor={bridgeDonor}
                            swapFailure={swapFailure}
                            rowRef={rowRef}
                            onShowAcceptsData={props.onShowAcceptsData}
                        />
                    );
                })}
            </>
        );
    };

    const renderMortalityRiskButton = (recipient: IRecipient) => {
        if (recipient.mortalityRiskPercent == 0 || recipient.mortalityRiskPercent == null || recipient.mortalityCalculations == null) {
            return '--';
        }

        return (
            <Button bsStyle='link' style={{ padding: 2 }} onClick={() => props.onShowMortalityBreakdown(recipient)}>{recipient.mortalityRiskPercent.toFixed(0)}%</Button>
        );
    };

    const renderFwtButton = (recipient: IRecipient) => {
        if (position.oneWay.recipient == null) {
            return '--';
        }

        return (
            <Button bsStyle='link' style={{ padding: 2 }} onClick={() => props.onShowFwt(recipient)}>{position.oneWay.recipient.fWait}</Button>
        );
    };

    const renderEwtButton = (summary: IEwtSummary, initialCount: any) => {
        if (position.oneWay.recipient == null) {
            return '--';
        }

        return (
            <Button bsStyle='link' style={{ padding: 2 }} onClick={() => props.onShowEwt(summary, initialCount)}>{position.oneWay.recipient.eWait}</Button>
        );
    };

    let { position, pathIndex, pathLength } = props;

    let oneWay = props.oneWays.find(x => x.donor.id == position.donorId && x.recipient.id == position.recipientId);
    let singleEpletMmRisk = props.epletMmRiskList.find(x => x.donorId == position.donorId && x.recipientId == position.recipientId);

    position.donor = oneWay.donor;
    position.recipient = oneWay.recipient;
    position.oneWay = oneWay;

    let dcSwapFailure = props.swapFailures.find(x => x.centerId == position.donor.center.id);
    let rcSwapFailure = props.swapFailures.find(x => x.centerId == position.recipient.center.id);
    let rdnCenterAlias = position.donor.rdnCenter != null ? position.donor.rdnCenter.alias : '';

    let intendedDonors = position.recipient.intendedDonors.filter(x => x.statusId == 5) //Find transplanted Intended Donor
    intendedDonors = intendedDonors.length > 0 ? intendedDonors : position.recipient.intendedDonors.filter(x => x.isExportable == true); //Use Exportable if no transplanted donor

    let ssdDonorList = position.oneWay?.ssdRecipientList ?? [];
    let currentRecipientId = position.oneWay?.recipient.id;

    let bgClass = null;

    if (ssdDonorList.length > 0 && !ssdDonorList.includes(currentRecipientId)) {
        bgClass = 'ssd-donor-recipient-cprA'; // donor is sole source for others, but NOT for this recipient
    }
    if (parseInt(oneWay.recipient.age) <= 21) {
        bgClass = 'pediatric-recipient';
    }

    let isFirstInChain = pathIndex == 0;

    return (
        <>
            <tr
                ref={rowRef}
                className={[
                    bgClass,
                    isFirstInChain ? 'chain-start-row' : ''
                ].filter(Boolean).join(' ')}
            >
                {props.showRexm == false && (
                    <td>{props.swapIndex}/{props.chainIndex}</td>
                )}
                <td>{position.distance}</td>
                <td>
                    {renderCenter(position.donor.center.alias, rdnCenterAlias, position.donor.center.isAdpPartner, dcSwapFailure)}
                </td>
                <td>
                    {renderChainStartPopover(position.distance, oneWay.donor)}
                </td>
                <td>
                    {renderCenter(position.recipient.center.alias, '', position.recipient.center.isAdpPartner, rcSwapFailure)}
                </td>
                <td>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div
                            onMouseOver={() => props.position.recipient != null ? props.onShowOverlay(props.position.donor, props.position.recipient, position.oneWay.donor.soleSourceDonors, position.oneWay.donor.ssdHtml, rowRef.current, intendedDonors) : null}
                            onMouseOut={() => props.onHideOverlay()}
                        >
                            <a href={'/center/' + position.recipient.center.id + '/recipient/' + position.recipientId + '/potential-donors'} target='_blank' style={{ marginRight: 3 }}>{position.recipient.alias}</a>
                        </div>
                        <div style={{ marginTop: '3px' }}>
                            <Glyphicon
                                glyph='cog'
                                style={{ margin: 0, padding: 0, lineHeight: 'initial' }}
                                onClick={handleShowEpletGraph}
                            />
                        </div>
                    </div>
                </td>
                <td className='position-abo'>{renderAbo()}</td>
                <td>{renderAges()}</td>
                <td>{position.oneWay.donor.donationTypeAbbreviation}&nbsp;:&nbsp;{renderRecipientType()}</td>
                <td>{position.oneWay.recipient.center.cprScore < 0 ? "N/A" : _.floor(position.oneWay.recipient.center.cprScore * 100.0)}</td>
                <td>{renderCpra4()}</td>
                <td>
                    <OverlayTrigger
                        rootClose
                        placement='right'
                        trigger='hover'
                        overlay={
                            <PopoverTemplate dismiss={false}>
                                The Donor Decline Ratio reflects the higher value between a patient's current decline ratio and their historic decline ratio in preselects from the past three months.
                            </PopoverTemplate>
                        }
                    >
                        {renderDDR()}
                    </OverlayTrigger>
                </td>
                <td>{position.oneWay.recipient.declineRatioCurrent.toFixed(0)}</td>
                <td>{position.oneWay.recipient.declineRatioHistoric.toFixed(0)}</td>
                <td><SingleEpletMmRisk data={singleEpletMmRisk} isAdmin={false} centerId={position.oneWay.recipient.centerId} showExpectedWaitTimeBlue={true} /></td>
                <td>{position.oneWay.hlaMatchScore}</td>
                <td><AlleleMatch bmtMm={position.oneWay.bmtMm} displayAsInline={false} /></td>
                <td>{renderHeights()}</td>
                <td>{position.oneWay.recipientWaitTime}</td>
                <td>{renderEwtButton(position.oneWay.recipient.ewtSummary, position.oneWay.recipient.initialCount)}</td>
                <td>{renderFwtButton(position.oneWay.recipient)}</td>
                <td>{renderMortalityRiskButton(position.oneWay.recipient)}</td>
                <td>{position.oneWay.recipient.dialysisMonths ?? '--'}</td>
                <td>{position.oneWay.distanceBetweenCenters}</td>

                {props.showRexm && (
                    <td>{renderExm()}</td>
                )}
            </tr>

            {props.matchRunType == MatchRunType.Chains && position.recipient.isInChip == false && pathIndex == pathLength - 1 && renderBridgeDonors()}
        </>
    );
}

export default PositionRow;